import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ApiStructBlock, ApiStructStep } from '../../utils/workoutStructure';
import {
  stepLengthSecs,
  structureBarHeightPct,
  stepZoneColor,
  stepZoneNumber,
  type StructureParseOptions,
} from '../../utils/workoutStructure';

import type { PrimaryIntensityMetric } from '../../utils/workoutBuilderSettings';

interface WorkoutStructureBarProps {
  structure?: ApiStructBlock[];
  totalDuration?: number;
  containerWidthPercent?: number;
  maxHeightPx?: number;
  barHeightPx?: number;
  layoutMode?: 'duration' | 'count';
  primaryIntensityMetric?: PrimaryIntensityMetric;
  ftpValue?: number;
  ltHrValue?: number;
  className?: string;
}

type FlatStep = {
  step: ApiStructStep;
  key: string;
};

/** Unwrap `{ type: 'step', steps: [leaf] }` shells used by palette blocks. */
export function resolveStructLeafStep(raw: ApiStructBlock | ApiStructStep): ApiStructStep {
  const step = raw as ApiStructStep & { steps?: ApiStructStep[] };
  const hasTargets =
    step.targets?.[0] != null &&
    (step.targets[0].minValue != null || step.targets[0].maxValue != null);
  if (hasTargets) return step;
  if (step.steps?.[0]) return resolveStructLeafStep(step.steps[0]);
  return step;
}

function flattenStructure(structure: ApiStructBlock[]): FlatStep[] {
  const entries: FlatStep[] = [];
  structure.forEach((block, blockIndex) => {
    const blockType = (block?.type ?? '').toLowerCase();
    const isRepetition = blockType === 'repetition' || blockType === 'repeat';
    const repetitions = isRepetition ? Number(block?.length?.value) || 1 : 1;
    for (let repIndex = 0; repIndex < repetitions; repIndex++) {
      (block?.steps ?? []).forEach((step, stepIndex) => {
        entries.push({
          step: resolveStructLeafStep(step as ApiStructBlock),
          key: `structure-${blockIndex}-${repIndex}-${stepIndex}`,
        });
      });
    }
  });
  return entries;
}

/** Horizontal intensity profile — stepped bars (ramps as stairs, steady as flat rects). */
export function WorkoutStructureBar({
  structure,
  totalDuration,
  containerWidthPercent = 92,
  maxHeightPx = 72,
  barHeightPx = 48,
  layoutMode = 'duration',
  primaryIntensityMetric,
  ftpValue = 0,
  ltHrValue = 0,
  className,
}: WorkoutStructureBarProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [cw, setCw] = useState(320);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = Math.floor(entries[0]?.contentRect.width ?? 0);
      if (w > 0) setCw(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const parseOptions: StructureParseOptions = useMemo(
    () => ({
      ftp: ftpValue,
      ltHr: ltHrValue,
      primaryIntensityMetric,
    }),
    [ftpValue, ltHrValue, primaryIntensityMetric],
  );

  const flatSteps = useMemo(
    () => (structure?.length ? flattenStructure(structure) : []),
    [structure],
  );

  const computedTotalDuration = useMemo(() => {
    if (!structure?.length || layoutMode !== 'duration') return 1;
    return (
      flatSteps.reduce((sum, { step }) => sum + stepLengthSecs(step.length), 0) || 1
    );
  }, [flatSteps, layoutMode, structure?.length]);

  const effectiveTotalDuration =
    layoutMode === 'duration'
      ? (totalDuration && totalDuration > 0 ? totalDuration : computedTotalDuration) || 1
      : flatSteps.length || 1;

  const scaleMax = useMemo(() => {
    const vals = flatSteps.map(({ step }) => structureBarHeightPct(step, parseOptions));
    return Math.max(100, ...vals, 1);
  }, [flatSteps, parseOptions]);

  const chart = useMemo(() => {
    if (!flatSteps.length) return null;

    const H = barHeightPx;
    const W = Math.max(cw, 40);
    const pctToH = (pct: number) => Math.max(0, Math.min((pct / scaleMax) * H, H));

    let cx = 0;
    const shapes: React.ReactNode[] = [];

    flatSteps.forEach(({ step, key }) => {
      const dur =
        layoutMode === 'duration'
          ? stepLengthSecs(step.length)
          : 1;
      const w =
        layoutMode === 'duration'
          ? Math.max((dur / effectiveTotalDuration) * W, 2)
          : Math.max(W / effectiveTotalDuration, 2);
      const color = stepZoneColor(step, parseOptions);
      const ox = cx;
      cx += w;

      const pct = structureBarHeightPct(step, parseOptions);
      const bh = Math.max(pctToH(pct), 2);
      shapes.push(
        <rect
          key={key}
          x={ox + 0.5}
          y={H - bh}
          width={Math.max(w - 1, 1)}
          height={bh}
          rx={Math.min(2, (w - 1) / 2)}
          data-training-zone={stepZoneNumber(step, parseOptions)}
          fill={color}
          style={{ fill: color }}
          opacity={1}
        />,
      );
    });

    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
        aria-hidden>
        {shapes}
      </svg>
    );
  }, [
    barHeightPx,
    cw,
    effectiveTotalDuration,
    flatSteps,
    layoutMode,
    parseOptions,
    scaleMax,
  ]);

  if (!structure?.length || !chart) return null;

  const rootClass = ['ltr-island', 'ws-structure-bar', className].filter(Boolean).join(' ');

  return (
    <div
      ref={ref}
      className={rootClass}
      dir="ltr"
      style={{
        width: `${containerWidthPercent}%`,
        height: barHeightPx,
        maxHeight: maxHeightPx,
        margin: '0 auto',
        overflow: 'hidden',
      }}>
      {chart}
    </div>
  );
}
